/**
 * useSearch Hook
 *
 * Core state management for Search page.
 * Matches Flutter's search_screen.dart logic:
 * - Dual user search (firstName + lastName) with deduplication
 * - Content tags search with infinite scroll pagination
 * - No debounce: uses isSearching flag + keyword change detection to retry
 * - Users: 6 per batch, "More people" / collapse
 * - Content tags: 9 per batch, infinite scroll
 */

import { useState, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import type { PageCursor } from '@/data/types/json';
import { createSearchUsersUseCase } from '@/domain/usecases/search/searchUsersUseCase';
import { createSearchContentTagsUseCase } from '@/domain/usecases/search/searchContentTagsUseCase';
import type { UserEntity } from '@/domain/entities/user/userEntity';
import type { ContentTagEntity } from '@/domain/entities/contentTag/contentTagEntity';
import { SERVER_FETCH_CONSTANTS } from '@/domain/constants/serverFetchConstants';
import { appLogger } from '@/domain/services/analytics/appLogger';
import type { RootState } from '@/presentation/store';

const SEARCH_USERS_LIMIT = SERVER_FETCH_CONSTANTS.SEARCH_USERS_PAGINATION_LIMIT;
const SEARCH_CONTENT_TAGS_LIMIT = SERVER_FETCH_CONSTANTS.SEARCH_PAGINATION_LIMIT;

export interface UseSearchResult {
  keyword: string;
  users: UserEntity[];
  contentTags: ContentTagEntity[];
  isSearching: boolean;
  hasMoreUsers: boolean;
  onSearchChanged: (text: string) => void;
  loadMoreUsers: () => void;
  collapseUsers: () => void;
  onScrollNearBottom: () => void;
}

export function useSearch(): UseSearchResult {
  // Current user — used by the search use case to drop blocked-relationship
  // rows from the results (see SearchUsersUseCase header comment).
  const currentUser = useSelector((state: RootState) => state.auth.user);
  const currentUserRef = useRef(currentUser);
  currentUserRef.current = currentUser;

  // State
  const [keyword, setKeyword] = useState('');
  const [users, setUsers] = useState<UserEntity[]>([]);
  const [contentTags, setContentTags] = useState<ContentTagEntity[]>([]);
  const [hasMoreUsers, setHasMoreUsers] = useState(true);
  const [isSearching, setIsSearching] = useState(false);

  // Refs for mutable state that shouldn't trigger re-renders
  const isSearchingRef = useRef(false);
  const keywordRef = useRef('');

  // Users pagination
  const allUsersRef = useRef<UserEntity[]>([]);
  const collapsedUsersRef = useRef<UserEntity[]>([]);
  const lastFetchedByFirstNameRef = useRef<PageCursor | undefined>(undefined);
  const lastFetchedByLastNameRef = useRef<PageCursor | undefined>(undefined);
  const hasMoreByFirstNameRef = useRef(true);
  const hasMoreByLastNameRef = useRef(true);
  const isFetchingUsersRef = useRef(false);
  const hasMoreUsersRef = useRef(true);

  // UseCase instances — created once to avoid unnecessary allocation per call
  const searchUsersUseCaseRef = useRef(createSearchUsersUseCase());
  const searchContentTagsUseCaseRef = useRef(createSearchContentTagsUseCase());

  // Content tags pagination
  const allContentTagsRef = useRef<ContentTagEntity[]>([]);
  const lastFetchedContentTagRef = useRef<PageCursor | undefined>(undefined);
  const hasMoreContentTagsRef = useRef(true);
  const isFetchingContentTagsRef = useRef(false);

  const resetSearch = useCallback(() => {
    allUsersRef.current = [];
    collapsedUsersRef.current = [];
    allContentTagsRef.current = [];
    hasMoreByFirstNameRef.current = true;
    hasMoreByLastNameRef.current = true;
    hasMoreUsersRef.current = true;
    hasMoreContentTagsRef.current = true;
    lastFetchedByFirstNameRef.current = undefined;
    lastFetchedByLastNameRef.current = undefined;
    lastFetchedContentTagRef.current = undefined;
    setUsers([]);
    setContentTags([]);
  }, []);

  const searchUsers = useCallback(async (searchValue: string) => {
    // Handle collapsed users first (re-expand)
    if (collapsedUsersRef.current.length > 0) {
      const toInclude = collapsedUsersRef.current.slice(0, SEARCH_USERS_LIMIT);
      collapsedUsersRef.current = collapsedUsersRef.current.slice(SEARCH_USERS_LIMIT);
      allUsersRef.current = [...allUsersRef.current, ...toInclude];
      setUsers([...allUsersRef.current]);
      return;
    }

    if (isFetchingUsersRef.current || !searchValue) return;

    if (!hasMoreUsersRef.current) {
      // Filter existing results locally
      if (allUsersRef.current.length > 0) {
        const lowerSearch = searchValue.toLowerCase();
        const filtered = allUsersRef.current.filter(
          user =>
            user.firstLastNameJoinedLowerCase.startsWith(lowerSearch) ||
            user.lastFirstNameJoinedLowerCase.startsWith(lowerSearch)
        );
        setUsers(filtered);
      }
      return;
    }

    isFetchingUsersRef.current = true;

    try {
      const result = await searchUsersUseCaseRef.current.execute({
        keyword: searchValue,
        limitCount: SEARCH_USERS_LIMIT,
        lastFetchedByFirstName: lastFetchedByFirstNameRef.current,
        lastFetchedByLastName: lastFetchedByLastNameRef.current,
        hasMoreByFirstName: hasMoreByFirstNameRef.current,
        hasMoreByLastName: hasMoreByLastNameRef.current,
        currentUser: currentUserRef.current,
      });

      // Deduplicate against existing users
      for (const user of result.users) {
        if (!allUsersRef.current.some(u => u.firebaseId === user.firebaseId)) {
          allUsersRef.current.push(user);
        }
      }

      lastFetchedByFirstNameRef.current = result.lastFetchedByFirstName;
      lastFetchedByLastNameRef.current = result.lastFetchedByLastName;
      hasMoreByFirstNameRef.current = result.hasMoreByFirstName;
      hasMoreByLastNameRef.current = result.hasMoreByLastName;
      hasMoreUsersRef.current = result.hasMoreByFirstName || result.hasMoreByLastName;
      setHasMoreUsers(hasMoreUsersRef.current);
      setUsers([...allUsersRef.current]);
    } catch (error) {
      appLogger.error('Error searching users', 'useSearch.searchUsers', { error: error as Error });
    } finally {
      isFetchingUsersRef.current = false;
    }
  }, []);

  const searchContentTags = useCallback(async (searchValue: string) => {
    if (isFetchingContentTagsRef.current || !searchValue) return;

    if (!hasMoreContentTagsRef.current) {
      // Filter existing results locally
      if (allContentTagsRef.current.length > 0) {
        const filtered = allContentTagsRef.current.filter(
          tag => tag.name.toLowerCase().startsWith(searchValue.toLowerCase())
        );
        setContentTags(filtered);
      }
      return;
    }

    isFetchingContentTagsRef.current = true;

    try {
      const result = await searchContentTagsUseCaseRef.current.execute(
        searchValue,
        lastFetchedContentTagRef.current,
        SEARCH_CONTENT_TAGS_LIMIT
      );

      // Deduplicate against existing
      for (const tag of result.contentTags) {
        if (!allContentTagsRef.current.some(t => t.name === tag.name)) {
          allContentTagsRef.current.push(tag);
        }
      }

      lastFetchedContentTagRef.current = result.cursor;
      hasMoreContentTagsRef.current = result.hasMore;
      setContentTags([...allContentTagsRef.current]);
    } catch (error) {
      appLogger.error('Error searching content tags', 'useSearch.searchContentTags', { error: error as Error });
    } finally {
      isFetchingContentTagsRef.current = false;
    }
  }, []);

  const search = useCallback(async (searchValue: string) => {
    isSearchingRef.current = true;
    setIsSearching(true);

    await Promise.all([
      searchUsers(searchValue),
      searchContentTags(searchValue),
    ]);

    isSearchingRef.current = false;
    setIsSearching(false);

    if (keywordRef.current.length > 0) {
      if (keywordRef.current !== searchValue) {
        resetSearch();
        search(keywordRef.current);
      }
    } else {
      resetSearch();
    }
  }, [searchUsers, searchContentTags, resetSearch]);

  const onSearchChanged = useCallback((text: string) => {
    if (text.length === 0) {
      keywordRef.current = '';
      setKeyword('');
      setUsers([]);
      setContentTags([]);
      resetSearch();
      return;
    }

    if (keywordRef.current === text) {
      // Backspace to same value - reset
      resetSearch();
    } else {
      keywordRef.current = text;
      setKeyword(text);
      if (!isSearchingRef.current) {
        search(text);
      }
    }
  }, [search, resetSearch]);

  const loadMoreUsers = useCallback(() => {
    if (keywordRef.current) {
      searchUsers(keywordRef.current);
    }
  }, [searchUsers]);

  const collapseUsers = useCallback(() => {
    if (allUsersRef.current.length > SEARCH_USERS_LIMIT) {
      collapsedUsersRef.current = allUsersRef.current.slice(SEARCH_USERS_LIMIT);
      allUsersRef.current = allUsersRef.current.slice(0, SEARCH_USERS_LIMIT);
      hasMoreUsersRef.current = true;
      setHasMoreUsers(true);
      setUsers([...allUsersRef.current]);
    }
  }, []);

  const onScrollNearBottom = useCallback(() => {
    if (
      isFetchingContentTagsRef.current ||
      !hasMoreContentTagsRef.current ||
      !keywordRef.current
    ) {
      return;
    }
    searchContentTags(keywordRef.current);
  }, [searchContentTags]);

  return {
    keyword,
    users,
    contentTags,
    isSearching,
    hasMoreUsers,
    onSearchChanged,
    loadMoreUsers,
    collapseUsers,
    onScrollNearBottom,
  };
}
